/**
 * Client for outage-safe support reports.
 * Order: SUPPORT_LAMBDA_URL → FastAPI /support/report → throw.
 * Callers must show product-facing copy only (no infra names in the UI).
 */

import axios from "axios";
import { CONFIG } from "../config";

const SUPPORT_LAMBDA_URL = (CONFIG.SUPPORT_LAMBDA_URL || "").replace(/\/$/, "");
const BACKEND_URL = (CONFIG.REACT_APP_BACKEND_URL || "http://localhost:8000/api").replace(
  /\/$/,
  "",
);

function buildBody(payload) {
  return {
    kind: payload.kind || "",
    status: payload.status != null ? String(payload.status) : "",
    request_id: payload.requestId || payload.request_id || "",
    path: payload.path || "",
    message: payload.message || "",
    error_code: payload.code || payload.error_code || "",
    api_method: payload.method || payload.api_method || "",
    api_url: payload.url || payload.api_url || "",
    timestamp: payload.timestamp || new Date().toISOString(),
    user_note: payload.userNote || payload.user_note || "",
    reply_to: payload.replyTo || payload.reply_to || "",
    user_agent:
      payload.userAgent ||
      (typeof navigator !== "undefined" ? navigator.userAgent : ""),
    website: "",
  };
}

async function postJson(url, body, { withCredentials }) {
  const res = await axios.post(url, body, {
    timeout: 20000,
    headers: { "Content-Type": "application/json" },
    withCredentials,
    validateStatus: () => true,
  });
  return res;
}

/**
 * POST a support diagnostic report.
 * Returns { success, message, channel: 'lambda' | 'api' }.
 */
export async function sendSupportReport(payload) {
  const body = buildBody(payload);
  const errors = [];

  if (SUPPORT_LAMBDA_URL) {
    try {
      const res = await postJson(`${SUPPORT_LAMBDA_URL}/`, body, {
        withCredentials: false,
      });
      const data = res.data || {};
      if (res.status >= 200 && res.status < 300 && data.success !== false) {
        return {
          success: true,
          message: data.message || "Support report sent.",
          channel: "lambda",
        };
      }
      errors.push(data.message || `Lambda HTTP ${res.status}`);
    } catch (err) {
      errors.push(err?.message || "Lambda unreachable");
    }
  }

  try {
    const res = await postJson(`${BACKEND_URL}/support/report`, body, {
      withCredentials: true,
    });
    const data = res.data || {};
    if (res.status >= 200 && res.status < 300) {
      return {
        success: true,
        message: data.message || "Support report sent.",
        channel: "api",
      };
    }
    const detail =
      (typeof data.detail === "string" && data.detail) ||
      data.message ||
      `API HTTP ${res.status}`;
    errors.push(detail);
  } catch (err) {
    errors.push(err?.message || "API unreachable");
  }

  // Keep technical detail on the Error for logs; UI maps to a generic message.
  const err = new Error(
    errors.filter(Boolean).join(" · ") || "Could not send support email.",
  );
  err.userMessage = "Couldn't send the support email.";
  throw err;
}
